/**
 * This slice is dedicated to Payments data.
 * It is using CRM, Pay and Reporting (rpt) APIs and 3rd party merchant API through Pay API.
 *
 * Quick dictionary:
 *  payment - a transaction (can be, deposit, withdrawal, adjustment) - Payments table in CRM DB
 *  linked account - a bank account (with IBAN or account number, bank name etc.) - LinkedAccounts table in CRM DB
 *  wire transfer - initiating a transaction - no DB representation
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CallStatus, LinkedAccountStatusEnum, PaymentMethodEnum, PaymentStatusEnum } from '../../enums';
import { GenericStatusByCall } from '../../enums/status-by-call';
import {
  GetPaymentsPayload,
  OwnerTradingAccount,
  Payment,
  PurchasePaymentTypeEnum,
  WithdrawalRequest,
} from '../../models/crm/types';
import { LinkedAccount } from '../../models/linked-account/types';
import {
  CardTransferStatusSuccessParam,
  GetCardTransferStatusSuccessResponse,
  GetCryptoRateParams,
  GetCryptoRateResponse,
  TransferFailResponse,
} from '../../models/payment/types';
import { cloneDeep } from '../../util/collectionHelpers';
import { isCallStatusPending } from '../../util/error-handling/StatusByCallHelpers';
import {
  ErrorPayloadFull,
  getAccountsAvailableCash,
  getAccountsAvailableCashFailed,
  getAccountsAvailableCashSuccess,
  getOwnerTradingAccountsFailed,
  loginFailed,
  logout,
  refreshPayments,
  refreshPaymentsDone,
} from '../common-actions';
import { GetAccountsAvailableCashResponse } from '../reporting/types';

import { ALL_TRANSACTIONS_PAGE_SIZE, INITIAL_TRANSACTIONS_STATE } from './constants';
import {
  calculateTotalPaymentAmount,
  getEurToUsdRate,
  isActiveOrWithdrawalRestrictedAccount,
  isPaymentBelongsToPlatformOrAdjustment,
  parsePayments,
  setCallForRefresh,
  setPaymentCallStatus,
} from './helpers';
import {
  GetSolanaPaymentsPayload,
  LastCardTransferType,
  PaymentCall,
  RefreshPaymentsPayload,
  RegisterCryptoLinkedAccountPayload,
  RegisterLinkedAccountPayload,
  TransactionsState,
  WebLastPaymentCache,
} from './types';

export interface PaymentState {
  isInitialPaymentsDataLoaded: boolean
  // TODO: Move to server calculation
  /**
   * Calculated when available cash is received and pending deposits summed up
   */
  positiveInstaCash: number
  availableCashInfo: GetAccountsAvailableCashResponse,
  // TODO: Move to server calculation
  /**
   * Used for temporary calculation of cash
   */
  pendingWithdrawalsTotalAmount: number
   /**
   * First 15 transactions of any kind
   */
  payments: TransactionsState
  /**
   * All transactions of any kind - for the last 5 years, limited to 1000
   */
  paymentsAll: TransactionsState
  /**
   * Bank accounts
   */
  linkedAccountIdByLastWithdrawal: null | number
  linkedAccounts: LinkedAccount[]
  cryptoLinkedAccounts: LinkedAccount[]
  /**
   * Bank account used for Bank Transfer (aka wire transfer)
   */
  /**
   * Account used for reference in calls and DB
   */
  tradingAccounts: OwnerTradingAccount[]
  /**
   * Neded for Web only.
   * Cached last payment (initially needed for Card Transfers only)
   * Web uses it when coming back from merchant redirect (DSK etc.)
   * and app is loaded from scratch
   */
  webLastCardTransfer: WebLastPaymentCache
  /**
    *Cached last payment (mostly used by mobile, Web uses `webLastCardTransfer`)
    */
  lastCardTransfer: LastCardTransferType
  /**
   * Marks if there is at least one Deposit performed by this user
   */
  hasDeposit: boolean
  /**
   * Marks if there is at least one Bank Account added by this user
   */
  hasBankAccount: boolean
  cryptoRate: GetCryptoRateResponse | null
  /**
   * True if there is at least one pending solana payment.
   * 'initial' means the fetch has not completed yet.
   */
  isSolanaPaymentPending: boolean | 'initial'
  /**
   * True if there is at least one rejected/canceled solana payment
   */
  isSolanaPaymentRejected: boolean
  /**
   * All pending solana payments returned by the last fetch (up to page_size).
   */
  pendingSolanaPayments: Payment[]
  /**
   * All rejected/canceled solana payments returned by the last fetch (up to page_size).
   */
  rejectedSolanaPayments: Payment[]
  /**
   * Payment calls register of statuses
   */
  statusByCall: GenericStatusByCall<PaymentCall>
  /**
   * State holding payment calls that need to be reloded
   * For example: getPayments, getAvailableCash etc.
   */
  callsForRefresh: RefreshPaymentsPayload | null
  errorsLog: string[],
}

export const INITIAL_PAYMENT_STATE: PaymentState = {
  isInitialPaymentsDataLoaded: false,
  availableCashInfo: {
    availableCash: [ {
      availableCashMultiCurrency: [],
      totalInBaseCurrency: {
        accountReferenceId: '',
        availableCash: 0,
        baseCurrency: '',
        currency: '',
        instaCash: 0,
        isArchivingActive: false,
        selectedCurrency: null,
      },
    } ],
    fxRates: [],
  },
  positiveInstaCash: 0,
  pendingWithdrawalsTotalAmount: 0,
  payments: INITIAL_TRANSACTIONS_STATE,
  paymentsAll: INITIAL_TRANSACTIONS_STATE,
  linkedAccountIdByLastWithdrawal: null,
  linkedAccounts: [],
  cryptoLinkedAccounts: [],
  tradingAccounts: [] as OwnerTradingAccount[],
  webLastCardTransfer: {},
  lastCardTransfer: {
    amount: 0,
  },
  cryptoRate: null,
  statusByCall: {},
  hasDeposit: false,
  hasBankAccount: false,
  isSolanaPaymentPending: 'initial',
  isSolanaPaymentRejected: false,
  pendingSolanaPayments: [],
  rejectedSolanaPayments: [],
  callsForRefresh: null,
  errorsLog: [],
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState: INITIAL_PAYMENT_STATE,
  reducers: {
    /** Meant to be called only by `checkInitialLoadEpic` in `payments/epics.ts` */
    confirmInitialPaymentsLoaded(state: PaymentState) {
      state.isInitialPaymentsDataLoaded = true;
    },
    getPayments(state: PaymentState, action: PayloadAction<GetPaymentsPayload>) {
      if (isCallStatusPending(PaymentCall.getPayments, state.statusByCall)) {
        const newPayload = cloneDeep(action.payload);
        newPayload.isQueued = true;
        state.payments.callsInQueue.push(newPayload);
        return;
      }

      const { isQueued, isAllTransactions } = action.payload;

      if (isQueued) state.payments.callsInQueue.shift();

      state.payments.isAllTransactionsCall = !!isAllTransactions;

      if (!isAllTransactions && action.payload.pageNumber === action.payload.defaultPageNumber) {
        setPaymentCallStatus(PaymentCall.getPayments, CallStatus.PENDING, state);
        state.payments.data.splice(0, state.payments.data.length);
      } else if (isAllTransactions) {
        setPaymentCallStatus(PaymentCall.getPaymentsAll, CallStatus.PENDING, state);
      }
    },
    getPaymentsCompleted(state: PaymentState, action: PayloadAction<Payment[]>) {
      const payments = action.payload?.filter(item => isPaymentBelongsToPlatformOrAdjustment(item));

      /**
       * If the records are `page_size + 1` it means that this is not last page (`page_size + 1` is BE indicator for more data available on next page)
       */
      const hasMoreData = payments.length > state.payments.pageSize;

      if (hasMoreData) {
        /**
         * remove the record which indicates that this is not last page
         */
        payments.pop();
      }

      parsePayments(payments, state.payments);
      setPaymentCallStatus(PaymentCall.getPayments, CallStatus.READY, state);
    },
    getPaymentsFailed(state: PaymentState, action: PayloadAction<ErrorPayloadFull>) {
      setPaymentCallStatus(PaymentCall.getPayments, CallStatus.ERROR, state, action.payload.status);
      state.payments = cloneDeep(INITIAL_TRANSACTIONS_STATE);
    },
    getPaymentsAllCompleted(state: PaymentState, action: PayloadAction<Payment[]>) {
      const receivedTransactions = action.payload?.filter(item => isPaymentBelongsToPlatformOrAdjustment(item));
      let deposits: Payment[] = [];
      let withdrawals: Payment[] = [];
      setPaymentCallStatus(PaymentCall.getPaymentsAll, CallStatus.READY, state);

      // parse all payments
      state.payments = cloneDeep(INITIAL_TRANSACTIONS_STATE);
      state.paymentsAll = cloneDeep(INITIAL_TRANSACTIONS_STATE);
      state.paymentsAll.pageSize = ALL_TRANSACTIONS_PAGE_SIZE;
      state.paymentsAll.filteredOut = [];
      state.paymentsAll.ignored = [];
      const { payments, paymentsAll } = state;
      parsePayments(receivedTransactions, paymentsAll, payments, deposits, withdrawals);

      const { availableCashInfo: { availableCash, fxRates }, positiveInstaCash: instaCash } = state;
      // process deposits
      if (deposits.length) state.hasDeposit = true;

      const eurToUsdRate = getEurToUsdRate(fxRates);
      // process withdrawals
      state.pendingWithdrawalsTotalAmount = calculateTotalPaymentAmount(withdrawals, eurToUsdRate);

      setPaymentCallStatus(PaymentCall.cashCalculated, CallStatus.UI_DONE, state);
    },
    getPaymentsAllFailed(state: PaymentState, action: PayloadAction<ErrorPayloadFull>) {
      state.paymentsAll = cloneDeep(INITIAL_TRANSACTIONS_STATE);
      setPaymentCallStatus(PaymentCall.getPaymentsAll, CallStatus.ERROR, state, action.payload.status);
      setPaymentCallStatus(PaymentCall.cashCalculated, CallStatus.ERROR, state);
    },
    getSolanaPayments(state: PaymentState, _action: PayloadAction<GetSolanaPaymentsPayload>) {
      setPaymentCallStatus(PaymentCall.getSolanaPayments, CallStatus.PENDING, state);
    },
    getSolanaPaymentsCompleted(
      state: PaymentState,
      action: PayloadAction<Payment[]>,
    ) {
      const rejectedStatuses = [
        PaymentStatusEnum.Canceled,
        PaymentStatusEnum.Rejected,
        PaymentStatusEnum.CanceledByClient,
      ];
      const payments = action.payload;
      const lastPurchasePlanPayment = payments.find(payment => payment.purchase_type_id === PurchasePaymentTypeEnum.Plan
        || payment.purchase_type_id === PurchasePaymentTypeEnum.PlanWithAutoPass);
      const lastPurchaseDailyRewindPayment = payments.find(payment => payment.purchase_type_id
        === PurchasePaymentTypeEnum.AccountDailyLossRewind);
      const lastPurchaseDeactivationAutoPassPayment = payments.find(payment => payment.purchase_type_id
         === PurchasePaymentTypeEnum.AutoPass);
      const lastPayments = [
        lastPurchasePlanPayment,
        lastPurchaseDailyRewindPayment,
        lastPurchaseDeactivationAutoPassPayment ].filter(Boolean) as Payment[];
      state.pendingSolanaPayments = lastPayments.length > 0 ? lastPayments.filter(
        payment => payment.payment_status
        === PaymentStatusEnum.Pending && payment.payment_method === PaymentMethodEnum.Solana,
      ) : [];

      state.rejectedSolanaPayments = payments.filter(
        payment => rejectedStatuses.includes(payment.payment_status)
        && payment.payment_method === PaymentMethodEnum.Solana,
      );

      state.isSolanaPaymentPending = state.pendingSolanaPayments.length > 0;
      state.isSolanaPaymentRejected = state.rejectedSolanaPayments.length > 0;

      setPaymentCallStatus(PaymentCall.getSolanaPayments, CallStatus.READY, state);
    },
    getSolanaPaymentsFailed(state: PaymentState) {
      state.isSolanaPaymentPending = false;
      setPaymentCallStatus(PaymentCall.getSolanaPayments, CallStatus.ERROR, state);
    },
    getIndividualLinkedAccounts(state) {
      setPaymentCallStatus(PaymentCall.getIndividualLinkedAccounts, CallStatus.PENDING, state);
    },
    getIndividualLinkedAccountsSuccess: (state, action) => {
      const activeAccounts = action.payload
        .filter((acc: LinkedAccount) => acc.status !== LinkedAccountStatusEnum.Inactive);
      const newAccounts = activeAccounts
        .filter((acc: LinkedAccount) => acc.payment_method !== PaymentMethodEnum.Solana);
      const newCryptoAccounts = activeAccounts
        .filter((acc: LinkedAccount) => acc.payment_method === PaymentMethodEnum.Solana);
      if (state.linkedAccounts.length !== newAccounts.length) {
      }
      state.linkedAccounts = newAccounts;
      state.cryptoLinkedAccounts = newCryptoAccounts;
      if (state.linkedAccounts.length === 0) {
        state.hasBankAccount = false;
      } else {
        state.hasBankAccount = state.linkedAccounts.length >= 1;
      }
      setPaymentCallStatus(PaymentCall.getIndividualLinkedAccounts, CallStatus.READY, state);
    },
    getIndividualLinkedAccountsFailed: (state, action: PayloadAction<ErrorPayloadFull>) => {
      setPaymentCallStatus(PaymentCall.getIndividualLinkedAccounts, CallStatus.ERROR, state, action.payload.status);
    },
    registerLinkedAccount(state, action: PayloadAction<RegisterLinkedAccountPayload>) {
      setPaymentCallStatus(PaymentCall.registerLinkedAccount, CallStatus.PENDING, state);
    },
    registerLinkedAccountSuccess: (state, action: PayloadAction<LinkedAccount>) => {
      setPaymentCallStatus(PaymentCall.registerLinkedAccount, CallStatus.READY, state);
      setCallForRefresh(state, PaymentCall.getIndividualLinkedAccounts);
    },
    registerLinkedAccountFailed: (state, action: PayloadAction<ErrorPayloadFull>) => {
      setPaymentCallStatus(PaymentCall.registerLinkedAccount, CallStatus.ERROR, state, action.payload.status);
    },
    registerCryptoLinkedAccount(state, action: PayloadAction<RegisterCryptoLinkedAccountPayload>) {
      setPaymentCallStatus(PaymentCall.registerCryptoLinkedAccount, CallStatus.PENDING, state);
    },
    registerCryptoLinkedAccountSuccess(state: PaymentState) {
      setPaymentCallStatus(PaymentCall.registerCryptoLinkedAccount, CallStatus.READY, state);
    },
    registerCryptoLinkedAccountFailed(state: PaymentState, action: PayloadAction<ErrorPayloadFull>) {
      setPaymentCallStatus(PaymentCall.registerCryptoLinkedAccount, CallStatus.ERROR, state, action.payload.status);
    },
    setRegisterCryptoLinkedAccountStatusToInitial(state: PaymentState) {
      setPaymentCallStatus(PaymentCall.registerCryptoLinkedAccount, CallStatus.INITIAL, state);
    },
    getOwnerTradingAccounts: (state: PaymentState) => {
      state.tradingAccounts = [] as OwnerTradingAccount[];
      setPaymentCallStatus(PaymentCall.getOwnerTradingAccounts, CallStatus.PENDING, state);
    },
    getOwnerTradingAccountsSuccess: (state: PaymentState, action: PayloadAction<OwnerTradingAccount[]>) => {
      state.tradingAccounts = action.payload.filter(
        account => isActiveOrWithdrawalRestrictedAccount(account),
      );
      setPaymentCallStatus(PaymentCall.getOwnerTradingAccounts, CallStatus.READY, state);
    },
    registerWithdrawalRequest: (state: PaymentState, action: PayloadAction<WithdrawalRequest>) => {
      setPaymentCallStatus(PaymentCall.registerWithdrawalRequest, CallStatus.PENDING, state);
    },
    registerWithdrawalRequestSuccess: (state: PaymentState, action: PayloadAction<WithdrawalRequest>) => {
      setPaymentCallStatus(PaymentCall.registerWithdrawalRequest, CallStatus.READY, state);
      setCallForRefresh(state, PaymentCall.getAvailableCash);
      setCallForRefresh(state, PaymentCall.getPaymentsAll);
    },
    registerWithdrawalRequestFail: (state: PaymentState, action: PayloadAction<ErrorPayloadFull>) => {
       
      setPaymentCallStatus(PaymentCall.registerWithdrawalRequest, CallStatus.ERROR, state, action.payload.errorData?.status);
    },
    registerCryptoWithdrawalRequest: (state: PaymentState, action: PayloadAction<WithdrawalRequest>) => {
      setPaymentCallStatus(PaymentCall.registerCryptoWithdrawalRequest, CallStatus.PENDING, state);
    },
    registerCryptoWithdrawalRequestSuccess: (state: PaymentState, action: PayloadAction<WithdrawalRequest>) => {
      setPaymentCallStatus(PaymentCall.registerCryptoWithdrawalRequest, CallStatus.READY, state);
      setCallForRefresh(state, PaymentCall.getAvailableCash);
      setCallForRefresh(state, PaymentCall.getPaymentsAll);
    },
    registerCryptoWithdrawalRequestFail: (state: PaymentState, action: PayloadAction<ErrorPayloadFull>) => {
       
      setPaymentCallStatus(PaymentCall.registerCryptoWithdrawalRequest, CallStatus.ERROR, state, action.payload.errorData?.status);
    },
    getCardTransferStatus(state: PaymentState, action: PayloadAction<CardTransferStatusSuccessParam>) {
      setPaymentCallStatus(PaymentCall.getCardTransferStatus, CallStatus.PENDING, state);
    },
    getCardTransferStatusSuccess(state: PaymentState, action: PayloadAction<GetCardTransferStatusSuccessResponse>) {
      setPaymentCallStatus(PaymentCall.getCardTransferStatus, CallStatus.READY, state);
      state.lastCardTransfer.statusCheckData = action.payload;
      setCallForRefresh(state, PaymentCall.getPaymentsAll);
      setCallForRefresh(state, PaymentCall.getAvailableCash);
    },
    getCardTransferStatusFail(state: PaymentState, action: PayloadAction<TransferFailResponse>) {
      setPaymentCallStatus(PaymentCall.getCardTransferStatus, CallStatus.ERROR, state, action.payload?.status);
      state.lastCardTransfer.transferFailedData = action.payload;
    },
    getCryptoRate(state: PaymentState, action: PayloadAction<GetCryptoRateParams | undefined>) {
      setPaymentCallStatus(PaymentCall.getCryptoRate, CallStatus.PENDING, state);
    },
    getCryptoRateSuccess(state: PaymentState, action: PayloadAction<GetCryptoRateResponse>) {
      setPaymentCallStatus(PaymentCall.getCryptoRate, CallStatus.READY, state);
      state.cryptoRate = action.payload;
    },
    getCryptoRateFail(state: PaymentState, action: PayloadAction<TransferFailResponse>) {
      setPaymentCallStatus(PaymentCall.getCryptoRate, CallStatus.ERROR, state, action.payload?.status);
    },
    /**
     * For marking UI state - a certain popup has been shown, redirect url used etc.
     */
    setBankAccountIdByLastWithdrawal(state: PaymentState, action) {
      state.linkedAccountIdByLastWithdrawal = action.payload;
    },
    setPaymentCallStatusToInitial: (state, action) => {
      setPaymentCallStatus(PaymentCall.registerLinkedAccount, CallStatus.INITIAL, state);
    },
  },
  extraReducers: {
    [loginFailed.type]: state => { state.isInitialPaymentsDataLoaded = false; state.isInitialPaymentsDataLoaded = false; },
    [getOwnerTradingAccountsFailed.type]: (state: PaymentState, action: PayloadAction<ErrorPayloadFull>) => {
       
      setPaymentCallStatus(PaymentCall.getOwnerTradingAccounts, CallStatus.ERROR, state, action.payload.errorData?.status);
    },
    [getAccountsAvailableCash.type]: (state: PaymentState) => {
      setPaymentCallStatus(PaymentCall.getAvailableCash, CallStatus.PENDING, state);
    },
    [getAccountsAvailableCashSuccess.type]: (
      state: PaymentState,
      action: PayloadAction<GetAccountsAvailableCashResponse>,
    ) => {
      const data = action.payload ?? [];
      if (!data.availableCash.length) {
        setPaymentCallStatus(PaymentCall.cashCalculated, CallStatus.UI_DONE, state);
        setPaymentCallStatus(PaymentCall.getAvailableCash, CallStatus.ERROR, state);
        return;
      }

      state.availableCashInfo = data;
      setPaymentCallStatus(PaymentCall.cashCalculated, CallStatus.UI_DONE, state);
      setPaymentCallStatus(PaymentCall.getAvailableCash, CallStatus.READY, state);
    },
    [getAccountsAvailableCashFailed.type]: (
      state: PaymentState,
      action: PayloadAction<ErrorPayloadFull>,
    ) => {
      setPaymentCallStatus(PaymentCall.getAvailableCash, CallStatus.ERROR, state, action.payload.status);
      setPaymentCallStatus(PaymentCall.cashCalculated, CallStatus.ERROR, state);
    },
    [refreshPayments.type]: (state: PaymentState, action: PayloadAction<RefreshPaymentsPayload>) => {
      const {
        [PaymentCall.cashCalculated]: willUpdateCash,
        [PaymentCall.getPaymentsAll]: willUpdateAllPayments,
      } = (action.payload ?? state.callsForRefresh) ?? {};
      if (willUpdateCash || willUpdateAllPayments) {
        setPaymentCallStatus(PaymentCall.cashCalculated, CallStatus.PENDING, state);
      }
    },
    [refreshPaymentsDone.type]: (state: PaymentState) => {
      state.callsForRefresh = null;
    },
    [logout.type]: state => {
      const newState = cloneDeep(INITIAL_PAYMENT_STATE);
      newState.errorsLog = state.errorsLog;

      return newState;
    },
  },
});

export const {
  confirmInitialPaymentsLoaded,
  getCardTransferStatus,
  getCardTransferStatusSuccess,
  getCardTransferStatusFail,
  getPayments,
  getPaymentsCompleted,
  getPaymentsFailed,
  getPaymentsAllCompleted,
  getPaymentsAllFailed,
  getSolanaPayments,
  getSolanaPaymentsCompleted,
  getSolanaPaymentsFailed,
  getIndividualLinkedAccounts,
  getIndividualLinkedAccountsFailed,
  getIndividualLinkedAccountsSuccess,
  registerLinkedAccount,
  registerLinkedAccountFailed,
  registerLinkedAccountSuccess,
  registerCryptoLinkedAccount,
  registerCryptoLinkedAccountSuccess,
  registerCryptoLinkedAccountFailed,
  setRegisterCryptoLinkedAccountStatusToInitial,
  getOwnerTradingAccounts,
  getOwnerTradingAccountsSuccess,
  registerWithdrawalRequest,
  registerWithdrawalRequestSuccess,
  registerWithdrawalRequestFail,
  registerCryptoWithdrawalRequest,
  registerCryptoWithdrawalRequestSuccess,
  registerCryptoWithdrawalRequestFail,
  setBankAccountIdByLastWithdrawal,
  setPaymentCallStatusToInitial,
  getCryptoRate,
  getCryptoRateSuccess,
  getCryptoRateFail,
} = paymentSlice.actions;

export default paymentSlice.reducer;
